import { z } from 'zod';
import { PlanEntry } from './planner.ts';

export const MUSCLE_GROUP_IDS = [
  'chest',
  'back',
  'shoulders',
  'biceps',
  'triceps',
  'forearms',
  'traps',
  'core',
  'lower_back',
  'glutes',
  'quads',
  'hamstrings',
  'adductors',
  'calves',
  'cardio',
] as const;
export const MuscleGroup = z.enum(MUSCLE_GROUP_IDS);
export type MuscleGroup = z.infer<typeof MuscleGroup>;

export const MUSCLE_GROUP_NAMES: Record<MuscleGroup, string> = {
  chest: 'Chest',
  back: 'Back',
  shoulders: 'Shoulders',
  biceps: 'Biceps',
  triceps: 'Triceps',
  forearms: 'Forearms',
  traps: 'Traps',
  core: 'Core',
  lower_back: 'Lower back',
  glutes: 'Glutes',
  quads: 'Quads',
  hamstrings: 'Hamstrings',
  adductors: 'Adductors',
  calves: 'Calves',
  cardio: 'Cardio',
};

/** What a set records: weight × reps, reps alone, a timed hold, or time/distance. */
export const ExerciseType = z.enum(['weight_reps', 'bodyweight_reps', 'hold', 'cardio']);
export type ExerciseType = z.infer<typeof ExerciseType>;

export const EXERCISE_TYPE_NAMES: Record<ExerciseType, string> = {
  weight_reps: 'Weight × reps',
  bodyweight_reps: 'Bodyweight reps',
  hold: 'Timed hold',
  cardio: 'Cardio (time / distance)',
};

const MuscleList = z.array(MuscleGroup).refine((l) => new Set(l).size === l.length, 'Duplicate muscle group');

export const Exercise = z.object({
  id: z.string(),
  name: z.string(),
  type: ExerciseType,
  primaryMuscles: z.array(MuscleGroup),
  secondaryMuscles: z.array(MuscleGroup),
  builtIn: z.boolean(),
  createdBy: z.string().nullable(),
  /** Custom exercises: how-to notes from whoever added it. */
  notes: z.string().nullable(),
  /** Custom exercises: the built-in exercise whose animation it borrows. */
  looksLike: z.string().nullable(),
});
export type Exercise = z.infer<typeof Exercise>;

export const ExerciseInput = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(60),
  type: ExerciseType,
  primaryMuscles: MuscleList.min(1, 'Pick at least one main muscle group').max(5),
  secondaryMuscles: MuscleList.max(10).default([]),
  notes: z.string().trim().max(2000).nullish().transform((v) => v || null),
  looksLike: z.string().max(100).nullish().transform((v) => v || null),
});
export type ExerciseInput = z.input<typeof ExerciseInput>;

export const ExerciseList = z.object({ exercises: z.array(Exercise) });
export const ExerciseResponse = z.object({ exercise: Exercise });

/** A named group of muscles to train on one day, e.g. "Chest & Triceps". Each person has their own. */
export const Split = z.object({
  id: z.string(),
  name: z.string(),
  muscleGroups: z.array(MuscleGroup),
  /** The template: the exercises a day of this split plans, in order (see planner.ts). */
  exercises: z.array(PlanEntry).default([]),
});
export type Split = z.infer<typeof Split>;

export const SplitInput = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(40),
  muscleGroups: MuscleList.min(1, 'Pick at least one muscle group'),
});
export type SplitInput = z.infer<typeof SplitInput>;

export const SplitList = z.object({ splits: z.array(Split) });
export const SplitResponse = z.object({ split: Split });

/** Quick-add suggestions for setting up splits. */
export const SUGGESTED_SPLITS: SplitInput[] = [
  { name: 'Chest & Triceps', muscleGroups: ['chest', 'triceps'] },
  { name: 'Back & Biceps', muscleGroups: ['back', 'biceps'] },
  { name: 'Shoulders & Biceps', muscleGroups: ['shoulders', 'biceps'] },
  { name: 'Legs', muscleGroups: ['quads', 'hamstrings', 'glutes', 'calves'] },
  { name: 'Push', muscleGroups: ['chest', 'shoulders', 'triceps'] },
  { name: 'Pull', muscleGroups: ['back', 'biceps', 'traps'] },
  { name: 'Upper body', muscleGroups: ['chest', 'back', 'shoulders', 'biceps', 'triceps'] },
  { name: 'Lower body', muscleGroups: ['quads', 'hamstrings', 'glutes', 'calves'] },
  { name: 'Full body', muscleGroups: ['chest', 'back', 'shoulders', 'quads', 'hamstrings', 'glutes', 'core'] },
  { name: 'Core', muscleGroups: ['core', 'lower_back'] },
  { name: 'Cardio', muscleGroups: ['cardio'] },
];
